/**
 * Les trois themes de l'affiche. Style de festival generique : aucun ne reprend
 * l'identite visuelle d'un festival existant (spec, section 1).
 *
 * Chaque theme est un jeu de jetons (couleurs, police, graisses) que poster.tsx pose
 * en variables CSS dans le SVG de l'affiche.
 */
export type ThemeId = "nuit" | "ete" | "atelier";

export interface PosterTheme {
  id: ThemeId;
  label: string;
  /** Famille declaree dans fonts.ts. */
  font: string;
  /** Graisse des titres et des tetes d'affiche. */
  strongWeight: number;
  /** Graisse des petits noms. */
  lightWeight: number;
  /** Espacement des lettres du titre, en em. */
  titleTracking: number;
  /** Rapport de corps entre les trois niveaux (tete d'affiche = 1). */
  tierScale: readonly [number, number, number];
  colors: {
    /** Fond : une couleur, ou les arrets d'un degrade vertical. */
    background: readonly string[];
    title: string;
    head: string;
    mid: string;
    small: string;
    separator: string;
    footer: string;
    ornament: string;
  };
}

export const THEMES: Record<ThemeId, PosterTheme> = {
  nuit: {
    id: "nuit",
    label: "Nuit",
    font: "Concerts Nuit",
    strongWeight: 700,
    lightWeight: 400,
    titleTracking: 0.06,
    tierScale: [1, 0.6, 0.42],
    colors: {
      background: ["#16080b", "#0b0a0e"],
      title: "#ff3b3b",
      head: "#ffffff",
      mid: "#efe9e1",
      small: "#b8b1a8",
      separator: "#ff3b3b",
      footer: "#efe9e1",
      ornament: "#ff3b3b",
    },
  },
  ete: {
    id: "ete",
    label: "Été",
    font: "Concerts Ete",
    strongWeight: 700,
    lightWeight: 400,
    titleTracking: 0.02,
    tierScale: [1, 0.6, 0.42],
    colors: {
      background: ["#ff6a5c", "#ff9f5a", "#ffd36b"],
      title: "#4a1530",
      head: "#3a0f26",
      mid: "#4a1530",
      small: "#3a0f26",
      separator: "#8c1040",
      footer: "#4a1530",
      ornament: "#fff4d6",
    },
  },
  atelier: {
    id: "atelier",
    label: "Atelier",
    font: "Concerts Atelier",
    strongWeight: 400,
    lightWeight: 400,
    titleTracking: 0.03,
    tierScale: [1, 0.58, 0.4],
    colors: {
      background: ["#f3ead6"],
      title: "#b3302a",
      head: "#1d2a3a",
      mid: "#b3302a",
      small: "#1d2a3a",
      separator: "#b3302a",
      footer: "#1d2a3a",
      ornament: "#1d2a3a",
    },
  },
};

export const THEME_IDS: readonly ThemeId[] = ["nuit", "ete", "atelier"];

export function isThemeId(value: unknown): value is ThemeId {
  return value === "nuit" || value === "ete" || value === "atelier";
}
